/**
 * APX WorkCenter — Xero Client Management
 * Accessible to: super_admin, controller
 *
 * Allows connecting/disconnecting each of the 3 Xero practice accounts
 * and syncing approved client organizations into the Hub client list.
 *
 * Workflow:
 * 1. Click "Connect Practice" → Xero OAuth opens in new tab
 * 2. In Xero, select which client organizations to authorize
 * 3. Return to WorkCenter → click "Sync Clients to WorkCenter"
 * 4. Synced clients appear in Client Analytics and Xero Reports immediately
 */

import React, { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";

// ─── Icons ────────────────────────────────────────────────────────────────────
function XeroIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
      <rect width="24" height="24" rx="5" fill="#13B5EA" />
      <path d="M7.5 12l2.5-2.5L12.5 12 10 14.5 7.5 12zm9 0l-2.5 2.5L11.5 12l2.5-2.5L16.5 12z" fill="white" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function UnlinkIcon() {
  return (
    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.181 8.68a4.503 4.503 0 011.903 6.405m-9.768-3.782L3.56 7.566a4.5 4.5 0 016.364-6.364l3.062 3.062m-3.367 3.367l-3.317 3.317m7.783 7.783L19.44 16.434a4.5 4.5 0 00-6.364-6.364l-3.062 3.062m-3.367 3.367l-3.317 3.317" />
    </svg>
  );
}

function SyncIcon() {
  return (
    <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// ─── Practice definitions ─────────────────────────────────────────────────────
const PRACTICES = [
  {
    key: "apx" as const,
    name: "APX Advisors LLC",
    label: "APX",
    loginEmail: "info@apx-advisors.com",
    note: "Use a dedicated browser session to avoid Xero cookie conflicts.",
    color: "oklch(0.72 0.14 75)",      // gold
    colorLight: "oklch(0.97 0.04 75)",
    colorBorder: "oklch(0.85 0.08 75)",
  },
  {
    key: "apx-hrb" as const,
    name: "APX-HRB",
    label: "APX-HRB",
    loginEmail: "apxadvisor@gmail.com",
    note: "Use a different browser or incognito window from APX.",
    color: "oklch(0.55 0.15 240)",     // blue
    colorLight: "oklch(0.96 0.04 240)",
    colorBorder: "oklch(0.82 0.08 240)",
  },
  {
    key: "apx-bbn" as const,
    name: "APX-BBN",
    label: "APX-BBN",
    loginEmail: "citifynanc@gmail.com",
    note: "Use a third browser session to avoid conflicts with APX and APX-HRB.",
    color: "oklch(0.50 0.15 170)",     // teal
    colorLight: "oklch(0.96 0.04 170)",
    colorBorder: "oklch(0.82 0.08 170)",
  },
];

// ─── Practice Card ────────────────────────────────────────────────────────────
function PracticeCard({
  practice,
  status,
  onRefresh,
}: {
  practice: typeof PRACTICES[number];
  status?: {
    connected: boolean;
    tenantName?: string;
    expiresAt?: string | Date | null;
    updatedAt?: string | Date | null;
    connectedAt?: string | Date | null;
    syncedClientCount?: number;
  };
  onRefresh: () => void;
}) {
  const { user } = useAuth();
  const [syncResult, setSyncResult] = useState<{ count: number } | null>(null);

  const authUrlQuery = trpc.xero.getAuthUrlForPractice.useQuery(
    { practiceKey: practice.key },
    { enabled: true }
  );

  const disconnectMutation = trpc.xero.disconnectPractice.useMutation({
    onSuccess: () => {
      toast.success(`${practice.label} disconnected from Xero.`);
      setSyncResult(null);
      onRefresh();
    },
    onError: (err) => toast.error(`Failed to disconnect: ${err.message}`),
  });

  const syncMutation = trpc.xeroPm.syncCoreClients.useMutation({
    onSuccess: (data) => {
      const practiceResult = data.perPractice.find(p => p.practiceKey === practice.key);
      const count = practiceResult?.count ?? 0;
      setSyncResult({ count });
      toast.success(`${practice.label}: ${count} client${count !== 1 ? "s" : ""} synced from Xero.`);
      onRefresh();
    },
    onError: (err) => toast.error(`Sync failed: ${err.message}`),
  });

  const isConnected = status?.connected ?? false;
  const clientCount = status?.syncedClientCount ?? 0;

  // Token expiry info
  const expiresAt = status?.expiresAt ? new Date(status.expiresAt) : null;
  const connectedAt = status?.connectedAt ? new Date(status.connectedAt) : null;
  const msUntilExpiry = expiresAt ? expiresAt.getTime() - Date.now() : null;
  const isExpiringSoon = msUntilExpiry !== null && msUntilExpiry < 30 * 60 * 1000; // < 30 min
  const isExpired = msUntilExpiry !== null && msUntilExpiry <= 0;

  const handleConnect = () => {
    const url = authUrlQuery.data?.url;
    if (!url) {
      toast.error("Could not generate Xero auth URL. Check that XERO_CLIENT_ID and XERO_CLIENT_SECRET are configured.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.info(
      `Xero login opened for ${practice.label}. Complete the login, select client organizations to authorize, then return here and click "Sync Clients to WorkCenter".`,
      { duration: 10000 }
    );
  };

  const handleDisconnect = () => {
    if (!confirm(`Disconnect ${practice.label} from Xero? This will stop all Xero reports for this practice until reconnected.`)) return;
    disconnectMutation.mutate({ callerEmail: user?.email ?? "", practiceKey: practice.key });
  };

  return (
    <div style={{
      background: "white",
      border: `1px solid ${isConnected ? practice.colorBorder : "oklch(0.88 0.015 240)"}`,
      borderRadius: "14px",
      marginBottom: "1.25rem",
      overflow: "hidden",
      boxShadow: isConnected ? `0 2px 12px ${practice.color}18` : "0 1px 4px oklch(0 0 0 / 5%)",
    }}>
      {/* Top accent bar */}
      <div style={{
        height: "4px",
        background: isConnected ? practice.color : "oklch(0.88 0.015 240)",
        opacity: isConnected ? 1 : 0.5,
      }} />

      <div style={{ padding: "1.4rem 1.5rem" }}>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" as const }}>
          <div style={{ flex: 1 }}>
            {/* Practice name + status badge */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.5rem", flexWrap: "wrap" as const }}>
              <span style={{ fontSize: "1.05rem", fontWeight: 800, color: "oklch(0.10 0.04 240)" }}>
                {practice.name}
              </span>
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.3rem",
                padding: "0.2rem 0.65rem",
                borderRadius: "9999px",
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                background: isConnected
                  ? (isExpired ? "oklch(0.95 0.08 30 / 20%)" : isExpiringSoon ? "oklch(0.95 0.10 55 / 20%)" : "oklch(0.88 0.12 145 / 20%)")
                  : "oklch(0.92 0.01 240 / 60%)",
                color: isConnected
                  ? (isExpired ? "oklch(0.40 0.15 30)" : isExpiringSoon ? "oklch(0.40 0.14 55)" : "oklch(0.28 0.12 145)")
                  : "oklch(0.50 0.03 240)",
                border: `1px solid ${isConnected
                  ? (isExpired ? "oklch(0.75 0.12 30 / 40%)" : isExpiringSoon ? "oklch(0.75 0.12 55 / 40%)" : "oklch(0.70 0.12 145 / 40%)")
                  : "oklch(0.80 0.02 240 / 40%)"}`,
              }}>
                {isConnected ? (
                  isExpired ? "⚠ Token Expired" :
                  isExpiringSoon ? "⚠ Expiring Soon" :
                  <><CheckCircleIcon /> Connected</>
                ) : "Disconnected"}
              </span>
            </div>

            {/* Login email */}
            <div style={{ fontSize: "0.8rem", color: "oklch(0.40 0.03 240)", fontFamily: "monospace", marginBottom: "0.5rem" }}>
              Xero login: <strong>{practice.loginEmail}</strong>
            </div>

            {/* Stats row — only when connected */}
            {isConnected && (
              <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" as const, marginBottom: "0.5rem" }}>
                {/* Client count */}
                <div style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.3rem 0.75rem",
                  borderRadius: "8px",
                  background: clientCount > 0 ? practice.colorLight : "oklch(0.95 0.01 240)",
                  border: `1px solid ${clientCount > 0 ? practice.colorBorder : "oklch(0.88 0.015 240)"}`,
                  fontSize: "0.82rem",
                  fontWeight: 700,
                  color: clientCount > 0 ? "oklch(0.20 0.05 240)" : "oklch(0.50 0.03 240)",
                }}>
                  <UsersIcon />
                  {clientCount > 0
                    ? <>{clientCount} client{clientCount !== 1 ? "s" : ""} synced</>
                    : "No clients synced yet"}
                </div>

                {/* Connected date */}
                {connectedAt && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.78rem", color: "oklch(0.50 0.03 240)" }}>
                    <ClockIcon />
                    Connected {connectedAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                )}
              </div>
            )}

            {/* Token expiry — only show if expiring soon or expired */}
            {isConnected && expiresAt && (isExpiringSoon || isExpired) && (
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.35rem",
                padding: "0.3rem 0.75rem",
                borderRadius: "7px",
                fontSize: "0.78rem",
                fontWeight: 600,
                background: isExpired ? "oklch(0.95 0.08 30 / 15%)" : "oklch(0.97 0.10 55 / 20%)",
                border: `1px solid ${isExpired ? "oklch(0.75 0.12 30 / 30%)" : "oklch(0.80 0.12 55 / 30%)"}`,
                color: isExpired ? "oklch(0.40 0.15 30)" : "oklch(0.40 0.14 55)",
                marginBottom: "0.4rem",
              }}>
                ⚠ Token {isExpired ? "expired" : `expires ${expiresAt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`} — reconnect to restore access
              </div>
            )}

            {/* Browser session note */}
            <div style={{ fontSize: "0.73rem", color: "oklch(0.55 0.03 240)", fontStyle: "italic" }}>
              ⚠ {practice.note}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" as const, marginTop: "1.1rem" }}>
          {isConnected ? (
            <>
              <button
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  padding: "0.5rem 1rem", borderRadius: "7px", fontSize: "0.84rem",
                  fontWeight: 600, cursor: "pointer", border: "1px solid",
                  background: practice.color, color: "oklch(0.10 0.04 240)",
                  borderColor: "oklch(0.60 0.12 75)",
                  transition: "opacity 0.15s",
                }}
                onClick={handleConnect}
              >
                <LinkIcon />
                Reconnect / Add More Clients
              </button>
              <button
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  padding: "0.5rem 1rem", borderRadius: "7px", fontSize: "0.84rem",
                  fontWeight: 600, cursor: "pointer", border: "1px solid",
                  background: "oklch(0.55 0.15 240)", color: "white",
                  borderColor: "oklch(0.48 0.15 240)",
                  opacity: syncMutation.isPending ? 0.7 : 1,
                }}
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending}
              >
                <SyncIcon />
                {syncMutation.isPending ? "Syncing…" : "Sync Clients to WorkCenter"}
              </button>
              <button
                style={{
                  display: "inline-flex", alignItems: "center", gap: "0.4rem",
                  padding: "0.5rem 1rem", borderRadius: "7px", fontSize: "0.84rem",
                  fontWeight: 600, cursor: "pointer", border: "1px solid",
                  background: "oklch(0.97 0.01 15)", color: "oklch(0.45 0.12 15)",
                  borderColor: "oklch(0.82 0.08 15)",
                  opacity: disconnectMutation.isPending ? 0.7 : 1,
                }}
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
              >
                <UnlinkIcon />
                Disconnect
              </button>
            </>
          ) : (
            <button
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                padding: "0.5rem 1.1rem", borderRadius: "7px", fontSize: "0.84rem",
                fontWeight: 600, cursor: "pointer", border: "1px solid",
                background: practice.color, color: "oklch(0.10 0.04 240)",
                borderColor: "oklch(0.60 0.12 75)",
              }}
              onClick={handleConnect}
            >
              <LinkIcon />
              Connect {practice.label}
            </button>
          )}
        </div>

        {/* Post-sync result */}
        {syncResult !== null && (
          <div style={{
            marginTop: "0.85rem",
            padding: "0.7rem 1rem",
            borderRadius: "8px",
            fontSize: "0.84rem",
            background: syncResult.count > 0 ? "oklch(0.88 0.12 145 / 10%)" : "oklch(0.95 0.04 55 / 15%)",
            border: `1px solid ${syncResult.count > 0 ? "oklch(0.70 0.12 145 / 30%)" : "oklch(0.80 0.10 55 / 30%)"}`,
            color: syncResult.count > 0 ? "oklch(0.25 0.10 145)" : "oklch(0.35 0.10 55)",
          }}>
            {syncResult.count > 0
              ? <>✅ <strong>{syncResult.count} client{syncResult.count !== 1 ? "s" : ""}</strong> synced from {practice.label} into the Hub client list.</>
              : <>⚠ No new organizations found — make sure you authorized clients in Xero during the connect step.</>
            }
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function XeroClientManagement() {
  const practiceStatuses = trpc.xero.practiceStatuses.useQuery();

  const getStatus = (key: string) => {
    return practiceStatuses.data?.find((p: { practiceKey: string }) => p.practiceKey === key);
  };

  const totalClients = practiceStatuses.data?.reduce((sum, p) => sum + (p.syncedClientCount ?? 0), 0) ?? 0;
  const connectedCount = practiceStatuses.data?.filter(p => p.connected).length ?? 0;

  return (
    <DashboardLayout>
      <div style={{ padding: "2rem", maxWidth: "860px", margin: "0 auto" }}>

        {/* Page header */}
        <div style={{ marginBottom: "1.75rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem" }}>
            <XeroIcon />
            <h1 style={{ fontSize: "1.45rem", fontWeight: 800, color: "oklch(0.08 0.04 240)", margin: 0 }}>
              Xero Client Management
            </h1>
          </div>
          <p style={{ fontSize: "0.875rem", color: "oklch(0.35 0.03 240)", lineHeight: 1.6, margin: 0 }}>
            Connect each practice account to Xero and sync client organizations into the Hub.
            Each practice uses a separate Xero login — use different browser sessions to avoid conflicts.
          </p>
        </div>

        {/* Summary bar */}
        {practiceStatuses.data && (
          <div style={{
            display: "flex",
            gap: "1rem",
            flexWrap: "wrap" as const,
            padding: "0.9rem 1.25rem",
            borderRadius: "10px",
            background: "oklch(0.96 0.015 240)",
            border: "1px solid oklch(0.88 0.02 240)",
            marginBottom: "1.5rem",
            fontSize: "0.85rem",
            color: "oklch(0.25 0.04 240)",
          }}>
            <span>
              <strong>{connectedCount} / 3</strong> practices connected
            </span>
            <span style={{ color: "oklch(0.70 0.02 240)" }}>·</span>
            <span>
              <strong>{totalClients}</strong> total clients synced across all practices
            </span>
          </div>
        )}

        {/* Workflow Instructions */}
        <div style={{
          padding: "1rem 1.25rem",
          borderRadius: "10px",
          background: "oklch(0.94 0.018 220)",
          border: "1px solid oklch(0.85 0.02 220)",
          fontSize: "0.84rem",
          color: "oklch(0.25 0.04 240)",
          lineHeight: 1.7,
          marginBottom: "1.5rem",
        }}>
          <strong>How to add a new client:</strong>
          <ol style={{ paddingLeft: "1.2rem", margin: "0.4rem 0 0" }}>
            <li>Log into Xero for the relevant practice and add the client organization there first.</li>
            <li>Return here and click <strong>"Reconnect / Add More Clients"</strong> for that practice.</li>
            <li>During the Xero OAuth screen, select the new client organization to authorize it.</li>
            <li>Click <strong>"Sync Clients to WorkCenter"</strong> — the client will appear in Client Analytics immediately.</li>
          </ol>
        </div>

        {/* Practice Cards */}
        {PRACTICES.map((practice) => (
          <PracticeCard
            key={practice.key}
            practice={practice}
            status={getStatus(practice.key)}
            onRefresh={() => practiceStatuses.refetch()}
          />
        ))}

        {/* Bottom notes */}
        <div style={{
          marginTop: "0.5rem",
          padding: "1rem 1.25rem",
          borderRadius: "10px",
          background: "oklch(0.94 0.018 220)",
          border: "1px solid oklch(0.85 0.02 220)",
          fontSize: "0.84rem",
          color: "oklch(0.25 0.04 240)",
          lineHeight: 1.7,
        }}>
          <strong>Important notes:</strong>
          <ul style={{ paddingLeft: "1.2rem", margin: "0.4rem 0 0" }}>
            <li>Each practice has its own Xero login and password — they must be connected separately.</li>
            <li>Use a different browser (or incognito window) for each practice to avoid Xero cookie conflicts.</li>
            <li>Organizations must be authorized individually in Xero — Xero does not allow bulk authorization.</li>
            <li>Synced clients are immediately available in Client Analytics and Xero Reports.</li>
            <li>Tokens auto-refresh every 20 minutes. If reports stop loading, use "Reconnect" to restore access.</li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
